// Imports for external libraries go here.
// Imports for external libraries go here.
import { FC, useRef, useState, useEffect, useCallback, memo, type RefObject } from 'react';
import clsx from 'clsx';
import Glide from '@glidejs/glide';

import { getDirection } from '../../modules/utils/overviewHelper';

import { CarouselWrapperPops } from './CarouselControls.types';
import { StyledCardSliderTrack, StyledCardSlideWrapper, StyledCardSliderWrapper } from './CarouselControls.styles';

// Use named rather than default exports.

export const CarouselWrapperComponent: FC<CarouselWrapperPops> = memo(props => {
  /**
   * carousel wrapper component
   */
  const {
    children,
    tabletColumns,
    desktopColumns,
    mobileColumns,
    totalSlideLen,
    wrapperId,
    carouselType3b,
    isMemberBenefitsSlider,
  } = props;
  const [isDesktop, setIsDesktop] = useState<boolean>();
  const [isTablet, setIsTablet] = useState<boolean>();
  const [isMobile, setIsMobile] = useState<boolean>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sliderRef = useRef<any>();

  const tabletCol = isNaN(parseInt(tabletColumns || '1')) ? 2 : parseInt(tabletColumns || '2');
  const desktopCol = isNaN(parseInt(desktopColumns || '4')) ? 4 : parseInt(desktopColumns || '4');
  const mobileCol = isNaN(parseInt(mobileColumns || '2')) ? 1 : parseInt(mobileColumns || '1');

  const [cardsPerView, setCardsPerView] = useState<number>(desktopCol);
  const leftArrowRef = useRef<HTMLButtonElement>(null);
  const rightArrowRef = useRef<HTMLButtonElement>(null);

  const handleResize = () => {
    /**
     * check with window size current view port
     */
    const mobileBreakpoint = window.matchMedia('(max-width:767px)');
    const tabletBreakpoint = window.matchMedia('(min-width: 768px) and (max-width: 1199px)');
    const desktopBreakpoint = window.matchMedia('(min-width: 1200px)');

    setIsDesktop(desktopBreakpoint?.matches);
    setIsTablet(tabletBreakpoint?.matches);
    setIsMobile(mobileBreakpoint?.matches);
  };

  const ArrowDisabler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Glide: any, Components: any) => {
      return {
        mount() {
          // Only in effect when rewinding is disabled
          // if (Glide.settings.rewind) {
          //   return;
          // }
          Glide.on(['mount.after', 'run'], () => {
            const component = sliderRef.current;
            if (component) {
              component.style.visibility = 'visible';
            }
            // Filter out arrows_control
            for (const controlItem of Components.Controls.items) {
              if (controlItem.className !== 'glide__arrows') {
                continue;
              }

              // Set left arrow state
              const left = controlItem.querySelector('.glide__arrow--left');
              const direction = getDirection();
              if (left) {
                if (direction === 'rtl' && Glide.index >= Components.Sizes.length - Glide.settings.perView) {
                  left.classList.add('is-disabled'); // Disable on first slide
                } else if (direction === 'ltr' && Glide.index === 0) {
                  left.classList.add('is-disabled'); // Disable on first slide
                } else {
                  left.classList.remove('is-disabled'); // Enable on other slides
                }
              }

              // Set right arrow state
              const right = controlItem.querySelector('.glide__arrow--right');
              if (right) {
                if (direction === 'rtl' && Glide.index === 0) {
                  right.classList.add('is-disabled'); // Disable on last slide
                } else if (direction === 'ltr' && Glide.index >= Components.Sizes.length - Glide.settings.perView) {
                  right.classList.add('is-disabled');
                } else {
                  right.classList.remove('is-disabled'); // Disable on other slides
                }
              }
            }
          });
        },
      };
    },
    []
  );

  useEffect(() => {
    if (isDesktop) {
      setCardsPerView(desktopCol);
    } else if (isTablet) {
      setCardsPerView(tabletCol);
    } else {
      setCardsPerView(mobileCol);
    }
    const direction = getDirection();

    const glide = new Glide(sliderRef.current, {
      type: 'slider',
      gap: isMemberBenefitsSlider ? 1 : 16,
      startAt: 0,
      autoplay: false,
      bound: true,
      direction: direction,
      focusAt: 0,
      dragThreshold: false,
      perView: desktopCol,
      peek: {
        before: 0,
        after: 0,
      },
      keyboard: false,
      classes: {
        disabledArrow: 'glide__arrow--disabled',
      },
      breakpoints: {
        1440: {
          perView: desktopCol,
          peek: {
            before: 0,
            after: 0,
          },
        },
        1199: {
          perView: tabletCol,
          peek: {
            before: 0,
            after: 0,
          },
        },
        767: {
          perView: mobileCol,
          peek: {
            before: 0,
            after: 0,
          },
        },
      },
    });

    glide.mount({ ArrowDisabler });

    const cleanupFns = [addClickTrackListeners(leftArrowRef), addClickTrackListeners(rightArrowRef)];

    return () => {
      cleanupFns.forEach(fn => fn());
    };
  }, [isDesktop, isTablet, isMobile, totalSlideLen, ArrowDisabler]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const addClickTrackListeners = (origRef: RefObject<HTMLElement>): (() => void) => {
    const handler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    origRef.current?.addEventListener('touchstart', handler);
    origRef.current?.addEventListener('click', handler);

    // on unmount, cleanup same handler reference
    return () => {
      origRef.current?.removeEventListener('touchstart', handler);
      origRef.current?.removeEventListener('click', handler);
    };
  };

  /**
   * neeed ti wrap common components as well for slider
   */
  return (
    <StyledCardSliderWrapper
      id={`slider-dynamic-card-wrapper-${wrapperId}`}
      className="glideOutlet carousel-card add-gutter-space-bottom"
      ref={sliderRef}
    >
      <StyledCardSliderTrack className="glide__track" data-glide-el="track">
        <StyledCardSlideWrapper
          className={clsx(
            'glide__slides row pt-1',
            !isMemberBenefitsSlider &&
              (!carouselType3b && cardsPerView && totalSlideLen && cardsPerView !== totalSlideLen ? 'pb-4' : 'pb-2')
          )}
        >
          {children}
        </StyledCardSlideWrapper>
      </StyledCardSliderTrack>
      <div
        className={clsx(
          'control-section',
          cardsPerView &&
            totalSlideLen &&
            cardsPerView < totalSlideLen &&
            cardsPerView !== totalSlideLen &&
            !isMemberBenefitsSlider
            ? 'carouselControlType1 align-items-center'
            : ''
        )}
      >
        {cardsPerView && totalSlideLen && cardsPerView !== totalSlideLen && (
          <>
            <div className="glide__arrows" data-glide-el="controls">
              <button
                className={clsx(
                  'glide__arrow--left',
                  !carouselType3b ? 'glide__arrow carousal-arrow' : '',
                  isMemberBenefitsSlider ? 'benefits-arrow-left' : 'left-arrow'
                )}
                data-glide-dir="<"
                aria-label="Previous"
                ref={leftArrowRef}
              >
                <span className="icon-arrow-left"></span>
              </button>
            </div>
            {!isMemberBenefitsSlider && (
              <div className="d-flex" data-glide-el="controls[nav]">
                {Array.from(
                  {
                    length: totalSlideLen - (cardsPerView - 1),
                  },
                  (_, i) => (
                    <button
                      className=""
                      data-glide-dir={i}
                      key={cardsPerView + i}
                      aria-label={`glide-bullet-${i}`}
                    ></button>
                  )
                )}
              </div>
            )}
            <div className="glide__arrows" data-glide-el="controls">
              <button
                className={clsx(
                  'glide__arrow--right',
                  !carouselType3b ? 'glide__arrow carousal-arrow' : '',
                  isMemberBenefitsSlider ? 'benefits-arrow-right' : 'right-arrow'
                )}
                data-glide-dir=">"
                aria-label="Next"
                ref={rightArrowRef}
              >
                <span className="icon-arrow-right"></span>
              </button>
            </div>
          </>
        )}
      </div>
    </StyledCardSliderWrapper>
  );
});
